import {
  doc, getDoc, setDoc, collection, query, where, getDocs, onSnapshot, orderBy, limit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db } from './firebase';
import type { TutorMode, TutorSession, TutorMessage, TutorQueryLimits } from '../types/tutor';
import type { AdaptationRecord } from '../types/adaptive';
import { TIER_LIMITS } from '../constants/tiers';
import { mutateCounters, recordGamificationAction } from './gamification';
import type { GamificationAction } from '../constants/points-config';

const MODE_TO_GAMIFICATION_ACTION: Partial<Record<TutorMode, GamificationAction>> = {
  explain_differently: 'tutor_mode_explain',
  go_deeper: 'tutor_mode_go_deeper',
  test_me: 'tutor_mode_test_me',
  drill_me: 'tutor_mode_test_me',
  connect_this: 'tutor_mode_connect',
  show_me: 'tutor_mode_explain',
};

const ADAPTATION_CONTENT_ID = '__adaptation__';
const ADAPTATION_PATH_ID = '__adaptation__';

// Lazy functions instance
let _functions: ReturnType<typeof import('firebase/functions').getFunctions> | null = null;
async function getFunctionsInstance() {
  if (!_functions) {
    const { getFunctions } = await import('firebase/functions');
    const { app } = await import('./firebase');
    _functions = getFunctions(app);
  }
  return _functions;
}

// ─── Query the Tutor ───

export async function sendTutorQuery(params: {
  userId: string;
  contentId: string;
  pathId: string;
  sessionId: string;
  query: string;
  mode: TutorMode;
  contextNodeId: string | null;
  highlightedText: string | null;
  /** Set true for pro/teams so a one-day gap can be auto-absorbed by a freeze. */
  tierAllowsFreezes?: boolean;
}): Promise<{ message: TutorMessage; resolvedMode: TutorMode; queriesRemaining: number } | { error: string; queriesUsed: number; queriesIncluded: number; limitType: string }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'handleTutorQuery');
  const result = await fn(params);
  const data = result.data as
    | { message: TutorMessage; resolvedMode: TutorMode; queriesRemaining: number }
    | { error: string; queriesUsed: number; queriesIncluded: number; limitType: string };

  if ('message' in data) {
    void recordGamificationAction(params.userId, 'tutor_query', {
      tickStreak: true,
      tierAllowsFreezes: params.tierAllowsFreezes,
    });
    const modeAction = MODE_TO_GAMIFICATION_ACTION[data.resolvedMode];
    if (modeAction) {
      void recordGamificationAction(params.userId, modeAction);
    }
    if (data.resolvedMode === 'go_deeper') {
      void mutateCounters(params.userId, { goDeeperModeCount: 1 });
    } else if (data.resolvedMode === 'test_me' || data.resolvedMode === 'drill_me') {
      void mutateCounters(params.userId, { testMeModeCount: 1 });
    }
  }

  return data;
}

// ─── Feedback ───

export async function submitTutorFeedback(
  sessionId: string,
  messageId: string,
  feedback: 'up' | 'down',
): Promise<void> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'handleTutorFeedback');
  await fn({ sessionId, messageId, feedback });
}

// ─── Checkpoint scoring ───

export async function submitTutorCheckpointAnswer(
  sessionId: string,
  messageId: string,
  answer: string,
): Promise<{ score: number; feedback: string; signalClassification: string }> {
  const functions = await getFunctionsInstance();
  const fn = httpsCallable(functions, 'scoreTutorCheckpoint');
  const result = await fn({ sessionId, messageId, userAnswer: answer });
  return result.data as { score: number; feedback: string; signalClassification: string };
}

// ─── Session management ───

export async function createTutorSession(params: {
  userId: string;
  contentId: string;
  pathId: string;
}): Promise<TutorSession> {
  const sessionId = `tutor_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const session: TutorSession = {
    sessionId,
    userId: params.userId,
    contentId: params.contentId,
    pathId: params.pathId,
    startedAt: new Date().toISOString(),
    endedAt: null,
    messages: [],
    activeNodeId: null,
    queriesInSession: 0,
  };

  await setDoc(doc(db, 'tutorSessions', sessionId), session);
  return session;
}

export async function startAdaptationDiscussion(params: {
  userId: string;
  contentId: string | null;
  pathId: string | null;
  adaptationRecordId: string | null;
}): Promise<TutorSession> {
  let recordContext: AdaptationRecord | null = null;
  if (params.adaptationRecordId) {
    const recSnap = await getDoc(
      doc(db, 'adaptive', params.userId, 'history', params.adaptationRecordId),
    );
    if (recSnap.exists()) recordContext = recSnap.data() as AdaptationRecord;
  }

  const sessionId = `tutor_adapt_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const session: TutorSession = {
    sessionId,
    userId: params.userId,
    contentId: params.contentId ?? ADAPTATION_CONTENT_ID,
    pathId: params.pathId ?? ADAPTATION_PATH_ID,
    startedAt: new Date().toISOString(),
    endedAt: null,
    messages: [],
    activeNodeId: null,
    queriesInSession: 0,
    adaptationDiscussion: true,
    adaptationRecordId: params.adaptationRecordId,
  };

  const persisted: Record<string, unknown> = { ...session };
  if (recordContext) persisted.adaptationRecordContext = recordContext;

  await setDoc(doc(db, 'tutorSessions', sessionId), persisted);
  return session;
}

export async function loadTutorSession(sessionId: string): Promise<TutorSession | null> {
  const ref = doc(db, 'tutorSessions', sessionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as TutorSession;
}

export async function loadPreviousSessions(
  userId: string,
  contentId: string,
  maxCount = 3,
): Promise<TutorSession[]> {
  const q = query(
    collection(db, 'tutorSessions'),
    where('userId', '==', userId),
    where('contentId', '==', contentId),
    orderBy('startedAt', 'desc'),
    limit(maxCount + 1), // +1 to exclude current if included
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as TutorSession);
}

export function onSessionUpdate(
  sessionId: string,
  callback: (session: TutorSession) => void,
): () => void {
  const ref = doc(db, 'tutorSessions', sessionId);
  return onSnapshot(ref, (snap) => {
    if (snap.exists()) callback(snap.data() as TutorSession);
  });
}

// ─── Query limits ───

export async function getTutorQueryLimits(userId: string): Promise<TutorQueryLimits> {
  const userDoc = await getDoc(doc(db, 'users', userId));
  const tier = (userDoc.data()?.subscription ?? 'free') as keyof typeof TIER_LIMITS;
  const limits = TIER_LIMITS[tier];

  const usageDoc = await getDoc(doc(db, 'usage', userId));
  const usage = usageDoc.data() ?? { lifetimeTutorQueries: 0, monthlyTutorQueries: 0 };

  const isLifetime = limits.tutorQueries.period === 'lifetime';
  const queriesUsed = isLifetime
    ? (usage.lifetimeTutorQueries as number ?? 0)
    : (usage.monthlyTutorQueries as number ?? 0);
  const queriesIncluded = limits.tutorQueries.included;

  return {
    tier,
    queriesUsed,
    queriesIncluded,
    canQuery: queriesUsed < queriesIncluded,
    limitType: queriesIncluded === Infinity ? 'unlimited' : isLifetime ? 'lifetime' : 'monthly',
  };
}
